import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import MFAEnrollmentFlow from '../components/MFAEnrollmentFlow';
import api from '../services/api';
import { getDeviceId } from '../utils/deviceId';

export default function EnrollMFAPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { completeLogin } = useAuth();

  // Must be declared before any conditional return — Rules of Hooks
  const tokenDataRef = React.useRef(null);

  const enrollmentToken = location.state?.enrollmentToken;

  // Guard: if no token in state, someone navigated here directly — send to login
  if (!enrollmentToken) {
    navigate('/login', { replace: true });
    return null;
  }

  const handleBegin = async () => {
    const response = await api.post('/auth/mfa-enrollment/begin', {
      enrollment_token: enrollmentToken,
    });
    return response.data;
  };

  const handleConfirm = async (secret, totpCode, trustDevice = false) => {
    const response = await api.post('/auth/mfa-enrollment/confirm', {
      enrollment_token: enrollmentToken,
      secret,
      totp_code: totpCode,
      trust_device: trustDevice,
      device_id: getDeviceId(),
    });
    return response.data;
  };

  const handleConfirmWithCapture = async (secret, totpCode, trustDevice) => {
    const data = await handleConfirm(secret, totpCode, trustDevice);
    tokenDataRef.current = { ...data, trustDevice };
    return data;
  };

  const handleSuccess = () => {
    const data = tokenDataRef.current;
    if (data) {
      // Only store the device token if the user opted in to trust this device
      const deviceToken = data.trustDevice ? data.device_token : null;
      completeLogin(data.access_token, data.refresh_token, data.user, deviceToken);
    }
    navigate('/dashboard', { replace: true });
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.title}>Nephr&#333;s</h1>
          <p style={styles.subtitle}>CKD Care Coordination</p>
        </div>
        <div style={styles.intro}>
          <div style={styles.introTitle}>Set up two-factor authentication</div>
          <p style={styles.introText}>
            Nephros requires two-factor authentication to protect patient data.
            This takes about two minutes and only needs to be done once.
          </p>
        </div>
        <MFAEnrollmentFlow
          onBegin={handleBegin}
          onConfirm={handleConfirmWithCapture}
          onSuccess={handleSuccess}
        />
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--color-bg-page)', fontFamily: 'var(--font-family-base)', padding: '24px' },
  card: { backgroundColor: 'var(--color-bg-card)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)', padding: '48px', width: '100%', maxWidth: '480px' },
  header: { textAlign: 'center', marginBottom: '32px' },
  title: { fontSize: '28px', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-nav)', margin: '0 0 4px 0', letterSpacing: '-0.5px', fontFamily: 'var(--font-family-base)' },
  subtitle: { fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', margin: 0, fontFamily: 'var(--font-family-base)' },
  intro: { marginBottom: '28px' },
  introTitle: { fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)', marginBottom: '8px', fontFamily: 'var(--font-family-base)' },
  introText: { fontSize: '13px', color: 'var(--color-text-muted)', lineHeight: '1.6', margin: 0, fontFamily: 'var(--font-family-base)' },
};